import i18n from 'i18next';
import type { AllowedButtons, Driver, DriveStep } from 'driver.js';
import { filterExistingSteps } from './tourFilter';
import { createGates } from './tourGates';
import { buildLiveSessionSteps, buildRolePlaySharedSteps } from './rolePlayWalkthroughSteps';
import { localizeSteps } from './tourI18n';

const noNext: { showButtons: AllowedButtons[] } = { showButtons: ['close'] };

// Pagination bars are the last element on these list pages. driver.js only
// scrolls a target into view when its own internal visibility check decides
// it isn't already fully on-screen (bounding rect vs window.innerHeight) —
// a bottom-of-page element flush with the viewport edge already passes that
// check, so driver.js skips scrolling entirely, leaving no room above for
// the `side: 'top'` popover and forcing an overlap. onHighlightStarted fires
// before driver.js positions the popover/stage, so forcing a centered
// scroll here (before driver.js's own scroll-or-skip decision) guarantees
// room on both sides regardless of that internal check.
function withCenteredHighlight(step: DriveStep): DriveStep {
  const prevOnHighlightStarted = step.onHighlightStarted;
  return {
    ...step,
    onHighlightStarted: (element, s, opts) => {
      element?.scrollIntoView({ block: 'center', behavior: 'auto' });
      prevOnHighlightStarted?.(element, s, opts);
    },
  };
}

/**
 * Join action clauses into one sentence: "a", "a, or b", "a, b, or c".
 *
 * Several list pages describe an Actions column whose buttons are conditional —
 * on role, on the active tab, or on each row's status — so their copy has to be
 * assembled from what is actually in the DOM rather than written as a fixed
 * string. Naming a button the viewer doesn't have is the bug this avoids.
 *
 * The comma before "or" is kept even for two clauses, matching the copy this was
 * extracted from (buildCompetitionsSteps): individual clauses can themselves
 * contain "or" ("start or continue a competition"), where the comma is what
 * separates the alternatives from each other.
 */
function joinClauses(clauses: string[]): string {
  if (clauses.length <= 1) return clauses[0] ?? '';
  return i18n.t('tutorials.clauseListOr', {
    head: clauses.slice(0, -1).join(', '),
    last: clauses[clauses.length - 1],
  });
}

/** Capitalise the first letter, for a clause list used at the start of a sentence. */
function sentence(text: string): string {
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : '';
}

/** Replace one step's popover copy in place, matched by its element selector. */
function reviseStep(
  steps: DriveStep[],
  element: string,
  popover: { title?: string; description: string },
): DriveStep[] {
  const index = steps.findIndex((step) => step.element === element);
  if (index === -1 || !steps[index].popover) return steps;
  const next = [...steps];
  next[index] = { ...next[index], popover: { ...next[index].popover, ...popover } };
  return next;
}

/**
 * Fully click-gated, mirroring the FTUE interactive tour (see
 * interactiveSteps.ts / rolePlayWalkthroughSteps.ts) rather than a
 * click-through Next-button tour — the user must actually perform each
 * action to advance. Ends differently from FTUE: instead of navigating away
 * via "Review Session" (which would destroy this tour before a closing step
 * could show), it has the user click "New Session" to reopen the same-page
 * scenario selector, then shows a real final closing step.
 *
 * Unlike FTUE (which always starts fresh with the scenario selector already
 * open), this tour can be triggered from the header dropdown at any time:
 * - After a previous session, when the selector is closed and the page is
 *   just waiting for an avatar click — a lead-in step has the user click a
 *   student avatar first (which reopens the selector), before continuing
 *   into the shared wizard walkthrough.
 * - While a session is already in progress (selector closed, mic button
 *   present) — clicking an avatar does nothing then (GeminiDirectStreamPage's
 *   avatar-click handler no-ops while connected), so the lead-in and the
 *   whole scenario wizard are skipped entirely in favor of jumping straight
 *   into the live-session steps (mic, step guide, toolbar, transcript).
 * - Mid-transition (selector closed, but the greeting preview is still
 *   playing or the session is still connecting) — an avatar click is also a
 *   no-op here, so a plain "wait a moment" step is shown instead, gated on
 *   the session actually connecting rather than on a click.
 * - The Session Ended modal is up (selector closed, session disconnected,
 *   modal covering the classroom scene) — an avatar click can't reach the
 *   scene at all, and no further gate would ever fire (the session is over,
 *   not "about to connect"), so the lead-in, the wizard, and the "End
 *   Session" step (that button no longer exists either) are all skipped in
 *   favor of jumping straight to the three session-end button steps, which
 *   are the only real targets left on screen.
 */
function buildRolePlayPageSteps(driverRef: React.RefObject<Driver | null>): DriveStep[] {
  const gates = createGates(driverRef);
  const { gateOnClickThenAppear, gateOnDom } = gates;

  const selectorAlreadyOpen = document.querySelector('#tour-scenario-selector') !== null;
  // #tour-end-session (GeminiDirectStreamPage.tsx) renders for the entire
  // life of a connected session, so it's a reliable "a session is already
  // live" signal on its own.
  //
  // #tour-classroom-scene also carries a data-tour-phase attribute reflecting
  // exactly which of the roleplay page's mutually-exclusive states is
  // current. Relying on #tour-end-session alone left two real transitional
  // phases unhandled — the intro-audio greeting preview, and the connect
  // handshake — where the selector is already closed but the session isn't
  // connected yet either. In that window an avatar click is a guaranteed
  // no-op (see handleAvatarSelect's guard), so the old fallback — "click a
  // student avatar to open the scenario selector", gated on the selector
  // reappearing — could never advance and hung the tour forever. data-tour-
  // phase distinguishes that window ('pre-connect'/'connecting') from truly
  // idle, so a "please wait" step can be shown and gated on the real next
  // transition instead.
  const phase = document.querySelector('#tour-classroom-scene')?.getAttribute('data-tour-phase');
  const endSessionPresent = document.querySelector('#tour-end-session') !== null;
  const sessionInProgress = !selectorAlreadyOpen && (endSessionPresent || phase === 'greeting' || phase === 'live');
  const isTransitioning =
    !selectorAlreadyOpen && !sessionInProgress && (phase === 'pre-connect' || phase === 'connecting');
  // The Session Ended modal is up: no session, and no session is coming
  // (unlike 'pre-connect'/'connecting', nothing here transitions on its own —
  // it waits on one of the three modal buttons). An avatar click can't reach
  // the classroom at all, so lead-in, wizard, and the "End Session" step
  // (whose button no longer exists) are all skipped below.
  const isEnded = !selectorAlreadyOpen && !sessionInProgress && !isTransitioning && phase === 'ended';

  const leadInSteps: DriveStep[] =
    selectorAlreadyOpen || sessionInProgress || isEnded
      ? []
      : isTransitioning
        ? [
            {
              element: '#tour-classroom-scene',
              popover: {
                title: 'tutorials.getStarted',
                description: 'tutorials.sessionStartingPleaseWait',
                ...noNext,
              },
              // No click is possible in this window (handleAvatarSelect no-ops
              // while connecting), so gate on the next real transition instead:
              // #tour-end-session mounts as soon as the session connects,
              // whichever of "greeting" or "live" it lands in next.
              ...gateOnDom(0, '#tour-end-session', 'appear'),
            },
          ]
        : [
            {
              element: '#tour-classroom-scene',
              popover: {
                title: 'tutorials.getStarted',
                description: 'tutorials.clickStudentAvatarOpen',
                ...noNext,
              },
              // Gate key 0 is unused elsewhere in this tour (the welcome step has
              // no gate) — it doesn't need to match this step's real position in
              // the array, only be unique within this createGates() call.
              ...gateOnDom(0, '#tour-scenario-selector', 'appear'),
            },
          ];

  // Skip the scenario wizard entirely when a session is already in progress —
  // its steps target the scenario selector, which won't reopen from an avatar
  // click while connected. During the transitional window, the wizard would
  // also target an already-closed selector, so treat it the same way. Once
  // the modal is up there's no wizard left to walk through either.
  const walkthroughSteps =
    sessionInProgress || isTransitioning
      // Jumping straight in here (no wizard immediately before it) — wait
      // for the mic button explicitly if opened during the greeting window
      // rather than silently skipping past it (see buildLiveSessionSteps's
      // waitForMic doc comment).
      ? buildLiveSessionSteps(gates, true)
      : isEnded
        ? []
        : buildRolePlaySharedSteps(gates);

  return [
    {
      popover: {
        title: 'tutorials.rolePlayPageTutorial',
        description: 'tutorials.detailedWalkthroughEverythingPage',
        showButtons: ['next', 'close'],
      },
    },
    ...leadInSteps,
    // Scenario wizard through live transcript (or just the live-session
    // portion, mid-session), shared with the FTUE interactive tour (see
    // rolePlayWalkthroughSteps.ts).
    ...walkthroughSteps,
    // #tour-end-session no longer exists once the Session Ended modal is up
    // — skip straight to the modal's own buttons below.
    ...(isEnded
      ? []
      : [
          {
            element: '#tour-end-session',
            popover: {
              title: 'common.endSession',
              description: 'tutorials.clickWhenReadyWrapUp',
              ...noNext,
            },
            // Clicking opens the Session Ended modal a render cycle later —
            // wait for its "New Session" button (not "Review Session" —
            // this tour stays on the page rather than navigating away) to
            // exist before advancing.
            ...gateOnClickThenAppear(10, '#tour-end-session', '#tour-session-end-tertiary'),
          },
        ]),
    // All three session-end buttons are informational only (Next-advance,
    // not click-gated) — actually clicking any of them for real here would
    // navigate away (Review Session), or stop and restart this very tour
    // (Practice Again / New Session: GeminiDirectStreamPage.tsx's
    // isTourActive handling can't yet tell "the Page Tutorial is active" apart
    // from "the FTUE interactive tour is active", so it always treats the
    // click as FTUE's stranded-tour recovery and restarts a fresh Page
    // Tutorial from step one instead of letting this one reach its closing
    // step). Unlike FTUE's own ending (a real click, since that tour has no
    // other way to know the user is done), the Page Tutorial can just say so
    // and move on.
    {
      element: '#tour-session-end-primary',
      popover: {
        title: 'common.reviewSession',
        description: 'tutorials.clickReviewSessionCheck',
        showButtons: ['next', 'close'],
      },
    },
    {
      element: '#tour-session-end-secondary',
      popover: {
        title: 'common.practiceAgain',
        description: 'tutorials.clickPracticeAgainJump',
        showButtons: ['next', 'close'],
      },
    },
    {
      element: '#tour-session-end-tertiary',
      popover: {
        title: 'tutorials.startAgain',
        description: 'tutorials.clickNewSessionPractice',
        showButtons: ['next', 'close'],
      },
    },
    {
      popover: {
        title: 'tutorials.thatsRolePlayPage',
        description: 'tutorials.clickTutorialButtonAnytime',
        showButtons: ['next', 'close'],
        doneBtnText: 'common.finish',
      },
    },
  ];
}

const SESSIONS_LIST_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'tutorials.feedbackPageTutorial',
      description: 'tutorials.detailedWalkthroughPage',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-sessions-search',
    popover: {
      title: 'tutorials.searchSessions',
      description: 'tutorials.searchIdNumberUser',
    },
  },
  {
    element: '#mode-filter',
    popover: {
      title: 'tutorials.filterMode',
      description: 'tutorials.narrowByMode',
    },
  },
  {
    element: '#type-filter',
    popover: {
      title: 'tutorials.filterType',
      description: 'tutorials.narrowByType',
    },
  },
  {
    element: '#tour-date-range-filter',
    popover: {
      title: 'tutorials.filterDate',
      description: 'tutorials.filterSessionsStartEnd',
    },
  },
  {
    element: '#tour-sessions-list',
    popover: {
      title: 'tutorials.sessionHistory',
      description: 'tutorials.browseAllPastRoleplay',
    },
  },
  {
    element: '#tour-sessions-actions-col',
    popover: {
      // Rewritten by buildSessionsListSteps when the row also carries the
      // request-review button; this copy is the transcript-only case, where
      // SessionsPage titles the column "Report", not "Actions".
      title: 'feedback.colReport',
      description: 'tutorials.eachRowHasTranscript',
    },
  },
  withCenteredHighlight({
    element: '#tour-sessions-pagination',
    popover: {
      title: 'tutorials.pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'tutorials.paginationSessions',
    },
  }),
  {
    popover: {
      title: 'tutorials.thatsFeedbackPage',
      description: 'tutorials.clickTutorialButtonAnytime',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
];

/**
 * The Actions column on this page holds one or two buttons, and the second one
 * is role-gated: SessionsPage renders `.request-review-button` ("Request human
 * review") only when `canRate(userRole)`. The step used to be titled "View
 * Transcript" and describe only that button, so an admin or trainer — the only
 * roles that can request a review — was walked past the control that starts the
 * whole human-review flow without it being named.
 *
 * filterExistingSteps' presence check on the `<th>` can't see this: the header
 * renders either way. So the copy is built from which buttons are actually in
 * the DOM, the same approach buildCompetitionsSteps takes for its own
 * conditional Actions column.
 */
function buildSessionsListSteps(): DriveStep[] {
  const steps = filterExistingSteps(SESSIONS_LIST_STEPS);

  // Anchored on the header, so the step survives an empty table; the buttons are
  // per-row, so only mention the review one when a row actually offers it.
  if (document.querySelector('.request-review-button') === null) return steps;

  return reviseStep(steps, '#tour-sessions-actions-col', {
    title: 'common.actions',
    description: 'tutorials.actionsTranscriptOrReview',
  });
}

// Session Detail has two layouts behind the header's Layout switch, "Side by side"
// and "Tabs" (see SessionDetailPage.tsx): layout A is the classic three-column page where
// everything is on screen at once; layout B (SessionDetailPageV2.tsx) splits
// the same content across Overview / Teaching Steps / Transcript tabs and
// unmounts whichever tab isn't active. The step arrays below are grouped by
// *where* their anchors live so buildSessionDetailSteps can order them per
// layout — flat presence-filtering is only safe for layout A.

// Page header + score-hero KPIs + the student sidebar: on both layouts these
// sit outside the tab content, so they're visible whichever tab is open.
const SESSION_DETAIL_HEADER_STEPS: DriveStep[] = [
  {
    element: '#session-feedback-heading',
    popover: {
      title: 'tutorials.sessionFeedbackPageTutorial',
      description: 'tutorials.detailedWalkthroughPage',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-kpi-steps',
    popover: {
      title: 'common.stepsCompleted',
      description: 'tutorials.seeHowManyTeaching',
    },
  },
  {
    element: '#tour-kpi-duration',
    popover: {
      title: 'common.duration',
      description: 'tutorials.howLongSessionLasted',
    },
  },
  {
    element: '#tour-kpi-end-reason',
    popover: {
      title: 'common.ended',
      description: 'tutorials.whySessionFinishedAll',
    },
  },
  {
    // Layout B's score hero only — filtered out on layout A, which has no
    // equivalent KPI card.
    element: '#tour-kpi-steps-included',
    popover: {
      title: 'common.stepsIncluded',
      description: 'tutorials.howManyTeachingSteps',
    },
  },
  {
    element: '#tour-kpi-rating',
    popover: {
      title: 'common.trainerRating',
      description: 'tutorials.showsWhetherTrainerHas',
    },
  },
];

// Layout A: the middle feedback column. Layout B: the "Overview" tab.
const SESSION_DETAIL_OVERVIEW_STEPS: DriveStep[] = [
  {
    element: '#tour-session-feedback',
    popover: {
      title: 'tutorials.aiCoachingFeedback',
      description: 'tutorials.readAiGeneratedCoaching',
    },
  },
  {
    element: '#tour-rate-controls',
    popover: {
      title: 'tutorials.rateFeedback',
      description: 'tutorials.trainersAdminsCanMark',
    },
  },
  {
    element: '#tour-session-steps',
    popover: {
      title: 'common.teachingSteps',
      description: 'tutorials.checklistTeachingMethodSee',
    },
  },
  {
    // Filtered out by filterExistingSteps on any module whose feedback block doesn't
    // evaluate tools — the same way the social-skills step below is.
    element: '#tour-session-tools',
    popover: {
      title: 'common.deEscalationTools',
      description: 'tutorials.whichDeEscalationTools',
    },
  },
  {
    element: '#tour-session-social-skills',
    popover: {
      title: 'common.socialSkillsFeedback',
      description: 'tutorials.howSessionWentEach',
    },
  },
  {
    // "Better ways to say it" and Speech metrics (SessionInsightsSections). Absent for a trial
    // user's session and one from before the feature, so filterExistingSteps drops them there;
    // while the insights are still generating, both sections already show a loading card.
    element: '#tour-session-line-feedback',
    popover: {
      title: 'tutorials.betterWaysToSayIt',
      description: 'tutorials.lineFeedbackForSession',
    },
  },
  {
    element: '#tour-session-speech-metrics',
    popover: {
      title: 'tutorials.speechMetrics',
      description: 'tutorials.speechMetricsForSession',
    },
  },
  {
    element: '#tour-session-recommendations',
    popover: {
      title: 'tutorials.recommendations',
      description: 'tutorials.getRecommendedResourcesBased',
    },
  },
];

const SESSION_DETAIL_SIDEBAR_STEP: DriveStep = {
  element: '#tour-student-column',
  popover: {
    title: 'tutorials.studentSessionInfo',
    description: 'tutorials.detailsAboutStudentProfile',
  },
};

const SESSION_DETAIL_TRANSCRIPT_STEPS: DriveStep[] = [
  {
    element: '#tour-session-transcript',
    popover: {
      title: 'common.transcript',
      description: 'tutorials.reviewFullConversationTranscript',
    },
  },
  {
    element: '#tour-transcript-search',
    popover: {
      title: 'tutorials.searchTranscript',
      description: 'tutorials.searchWithinConversationTranscript',
    },
  },
];

/**
 * Human Review (SessionReviewPanel). What renders depends on who's looking and
 * on review state: trainers/admins get the request → assign → review workflow,
 * a trainee gets a read-only "Coach Review" card (and nothing at all until a
 * review has been submitted). So every step here goes through
 * filterExistingSteps and only the blocks actually on screen are toured — the
 * two role-specific roots are mutually exclusive by design.
 */
const SESSION_DETAIL_HUMAN_REVIEW_STEPS: DriveStep[] = [
  {
    element: '#tour-session-human-review',
    popover: {
      title: 'common.humanReview',
      description: 'tutorials.beyondAiFeedbackSession',
    },
  },
  {
    element: '#tour-session-coach-review',
    popover: {
      title: 'common.coachReview',
      description: 'tutorials.coachHasReviewedSession',
    },
  },
  {
    element: '#tour-review-request',
    popover: {
      title: 'tutorials.requestReview',
      description: 'tutorials.setPriorityOptionallyNote',
    },
  },
  {
    element: '#tour-review-workspace',
    popover: {
      title: 'common.addYourReview',
      description: 'tutorials.whenSessionAssignedRate',
    },
  },
  {
    element: '#tour-review-completed',
    popover: {
      title: 'common.humanFeedback',
      description: 'tutorials.submittedReviewsCollectedEach',
    },
  },
];

// Layout B's "Teaching Steps" tab — the expanded timeline behind the Overview
// tab's compact checklist. No layout A equivalent.
const SESSION_DETAIL_STEPS_TIMELINE_STEP: DriveStep = {
  element: '#tour-steps-timeline',
  popover: {
    title: 'tutorials.stepByStepTimeline',
    description: 'tutorials.everyTeachingStepOrder',
  },
};

const SESSION_DETAIL_AB_TOGGLE_STEP: DriveStep = {
  element: '#tour-ab-toggle',
  popover: {
    title: 'tutorials.switchLayouts',
    description: 'tutorials.twoWaysReadPage',
  },
};

const SESSION_DETAIL_CLOSING_STEP: DriveStep = {
  popover: {
    title: 'tutorials.thatsSessionFeedbackPage',
    description: 'tutorials.clickTutorialButtonAnytime',
    showButtons: ['next', 'close'],
    doneBtnText: 'common.finish',
  },
};

// Distinct cleanup keys for the gates below — see createGates, where the index
// is a map key per gated step, not a position in the step list.
const GATE_OVERVIEW_TAB = 0;
const GATE_STEPS_TAB = 1;
const GATE_TRANSCRIPT_TAB = 2;

/**
 * Layout A (classic) puts everything on one screen, so a flat
 * filterExistingSteps pass covers it.
 *
 * Layout B (SessionDetailPageV2.tsx) is tabbed and unmounts the inactive tabs,
 * so its tour walks the tab bar left to right — Overview, Teaching Steps,
 * Transcript — with a click-gated step opening each tab before touring its
 * contents, mirroring the Scene Editor toolbar fix (see buildSceneEditorSteps
 * above). Two consequences worth spelling out:
 *
 *  - Human Review lives at the bottom of the *Overview* tab, so it must be
 *    toured before any tab switch, not after the transcript as on layout A.
 *  - The tutorial can be opened from any tab, so "Overview is active" is an
 *    assumption to check, not a given — hence the gate back onto it.
 *
 * Steps behind a not-yet-open tab deliberately skip filterExistingSteps: their
 * anchors don't exist at build time, and the gate ahead of them guarantees the
 * tab is mounted before they're reached.
 */
function buildSessionDetailSteps(driverRef: React.RefObject<Driver | null>): DriveStep[] {
  const isTabbedLayout =
    document.querySelector('#tour-v2-tab-overview, #tour-v2-tab-steps, #tour-v2-tab-transcript') !== null;

  if (!isTabbedLayout) {
    return filterExistingSteps([
      ...SESSION_DETAIL_HEADER_STEPS,
      ...SESSION_DETAIL_OVERVIEW_STEPS,
      SESSION_DETAIL_SIDEBAR_STEP,
      ...SESSION_DETAIL_TRANSCRIPT_STEPS,
      ...SESSION_DETAIL_HUMAN_REVIEW_STEPS,
      SESSION_DETAIL_AB_TOGGLE_STEP,
      SESSION_DETAIL_CLOSING_STEP,
    ]);
  }

  const { gateOnClickThenAppear } = createGates(driverRef);
  const steps: DriveStep[] = filterExistingSteps(SESSION_DETAIL_HEADER_STEPS);

  const overviewTab = document.querySelector('#tour-v2-tab-overview');
  const overviewContent = [...SESSION_DETAIL_OVERVIEW_STEPS, ...SESSION_DETAIL_HUMAN_REVIEW_STEPS];
  if (document.querySelector('#tour-session-feedback') === null && overviewTab !== null) {
    steps.push(
      {
        element: '#tour-v2-tab-overview',
        popover: {
          title: 'common.overview',
          description: 'tutorials.clickOverviewTabTour',
          ...noNext,
        },
        ...gateOnClickThenAppear(GATE_OVERVIEW_TAB, '#tour-v2-tab-overview', '#tour-session-feedback'),
      },
      ...overviewContent,
    );
  } else {
    steps.push(...filterExistingSteps(overviewContent));
  }

  steps.push(...filterExistingSteps([SESSION_DETAIL_SIDEBAR_STEP]));

  if (document.querySelector('#tour-v2-tab-steps') !== null) {
    steps.push(
      {
        element: '#tour-v2-tab-steps',
        popover: {
          title: 'tutorials.teachingStepsTab',
          description: 'tutorials.clickTeachingStepsTab',
          ...noNext,
        },
        ...gateOnClickThenAppear(GATE_STEPS_TAB, '#tour-v2-tab-steps', '#tour-steps-timeline'),
      },
      SESSION_DETAIL_STEPS_TIMELINE_STEP,
    );
  }

  if (document.querySelector('#tour-v2-tab-transcript') !== null) {
    steps.push(
      {
        element: '#tour-v2-tab-transcript',
        popover: {
          title: 'common.transcript',
          description: 'tutorials.clickTranscriptTabReview',
          ...noNext,
        },
        ...gateOnClickThenAppear(GATE_TRANSCRIPT_TAB, '#tour-v2-tab-transcript', '#tour-session-transcript'),
      },
      ...SESSION_DETAIL_TRANSCRIPT_STEPS,
    );
  }

  steps.push(...filterExistingSteps([SESSION_DETAIL_AB_TOGGLE_STEP]), SESSION_DETAIL_CLOSING_STEP);
  return steps;
}

const MY_PROGRESS_INTRO_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.myProgress',
      description: 'tutorials.trackXpStreaksBadges',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.tabs',
      description: 'tutorials.switchBetweenOverviewLeaderboard',
    },
  },
];

const MY_PROGRESS_OVERVIEW_STEPS: DriveStep[] = [
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.profileBanner',
      description: 'tutorials.quickSnapshotTrainingProfile',
    },
  },
  {
    element: '#tour-progress-stats',
    popover: {
      title: 'tutorials.statsGlance',
      description: 'tutorials.totalXpStreakBadges',
    },
  },
  {
    element: '#tour-progress-streak',
    popover: {
      title: 'tutorials.practiceStreak',
      description: 'tutorials.keepPracticeStreakAlive',
    },
  },
  {
    element: '#tour-progress-xp-level',
    popover: {
      title: 'tutorials.xpLevel',
      description: 'tutorials.trackXpProgressToward',
    },
  },
  {
    element: '#tour-progress-next-badges',
    popover: {
      title: 'tutorials.nextBadges',
      description: 'tutorials.seeHowCloseEarning',
    },
  },
  {
    element: '#tour-progress-activity-heatmap',
    popover: {
      title: 'tutorials.activity',
      description: 'tutorials.dayByDayActivity',
    },
  },
  {
    element: '#tour-progress-difficulty-breakdown',
    popover: {
      title: 'tutorials.sessionsDifficulty',
      description: 'tutorials.breakdownCompletedSessionsDifficulty',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
];

// The leaderboard header already differs by role (see MyProgressPage.tsx:
// "Rankings across all orgs" for super admins vs. "Rankings at {org}" for
// everyone else) and marks which one it shows in data-scope, so the tour copy
// reads that same element rather than guessing the viewer's role independently —
// keeps the two in sync without pageSteps.ts knowing anything about roles, and
// works in every language since it doesn't match the header's text.
function buildLeaderboardSteps(): DriveStep[] {
  const headerScope = document.querySelector<HTMLElement>('#tour-progress-leaderboard-header')?.dataset.scope;
  const scope = i18n.t(headerScope === 'all-orgs' ? 'tutorials.scopeAllOrganizations' : 'tutorials.scopeYourOrganization');

  return [
    {
      element: '#tour-progress-leaderboard-header',
      popover: {
        title: 'tutorials.rankings',
        description: i18n.t('tutorials.seeHowYouRank', { scope }),
      },
    },
    {
      element: '#tour-progress-leaderboard-table',
      popover: {
        title: 'tutorials.leaderboard',
        description: 'tutorials.topPerformersAppearPodium',
        showButtons: ['next', 'close'],
        doneBtnText: 'common.finish',
      },
    },
  ];
}

const MY_PROGRESS_BADGES_STEPS: DriveStep[] = [
  {
    element: '#tour-progress-badges-header',
    popover: {
      title: 'tutorials.milestones',
      description: 'tutorials.trackHowManyBadges',
    },
  },
  {
    element: '#tour-progress-badges-grid',
    popover: {
      title: 'common.badges',
      description: 'tutorials.earnedBadgesHighlightedLocked',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
];

// Overview, Leaderboard, and Badges are separate tabs whose content is
// conditionally rendered (not just hidden) — only the active tab's elements
// exist in the DOM. A single static step list would only ever show whichever
// tab happened to be open when the tour starts, so the tab-specific steps are
// picked based on which tab's content is actually present at tour-open time.
function buildMyProgressSteps(): DriveStep[] {
  const steps = filterExistingSteps(MY_PROGRESS_INTRO_STEPS);

  if (document.querySelector('#tour-progress-leaderboard-header') !== null) {
    return [...steps, ...filterExistingSteps(buildLeaderboardSteps())];
  }
  if (document.querySelector('#tour-progress-badges-header') !== null) {
    return [...steps, ...filterExistingSteps(MY_PROGRESS_BADGES_STEPS)];
  }
  return [...steps, ...filterExistingSteps(MY_PROGRESS_OVERVIEW_STEPS)];
}

const ANALYTICS_DASHBOARD_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.analyticsDashboard',
      description: 'tutorials.viewAnalyticsReportingAcross',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'common.refresh',
      description: 'tutorials.refreshAnalyticsData',
    },
  },
  {
    element: '#tour-filter-bar',
    popover: {
      title: 'tutorials.filters',
      description: 'tutorials.narrowDataDownDate',
    },
  },
  {
    element: '#tour-kpi-grid',
    popover: {
      title: 'tutorials.kpis',
      description: 'tutorials.keyNumbersSummarizingTraining',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.charts',
      description: 'tutorials.exploreTrainingPerformanceSession',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
];

const USERS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.users',
      description: 'tutorials.manageUsersOrganization',
      showButtons: ['next', 'close'],
    },
  },
  {
    // Sits left of Add User in the header, so it is toured first.
    // Admins and super admins only — filterExistingSteps drops it for everyone else.
    element: '#tour-users-import',
    popover: {
      title: 'tutorials.importUsers',
      description: 'tutorials.createManyUsersOnce',
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'common.addUser',
      description: 'tutorials.inviteCreateNewUser',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.userList',
      description: 'tutorials.browseManageExistingUsers',
    },
  },
  {
    // The per-row cell, not the `<th>` header — on narrow viewports the
    // table scrolls horizontally to reach the Actions column (see
    // UsersPage.scss), and the header can end up off-screen or (mirroring
    // the Assignments pages' same fix below) scrolled to unreliably. The
    // per-row cell is what's actually being scrolled into view.
    //
    // Scoped under `#tour-page-content` (not a bare `.actions-cell`) — the
    // loading skeleton's placeholder row (UserTableSkeleton in
    // UsersPage.tsx) reuses the exact same `.actions-cell` class, and
    // `document.querySelector` returns the first match in document order.
    // Only the real table's container carries `#tour-page-content`, so this
    // can never resolve to the skeleton even if the tutorial is opened while
    // the list is still loading.
    element: '#tour-page-content .actions-cell',
    popover: {
      title: 'common.actions',
      description: 'tutorials.editRemoveDeleteUser',
    },
  },
  withCenteredHighlight({
    element: '#tour-users-pagination',
    popover: {
      title: 'tutorials.pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'tutorials.paginationUsers',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  }),
];

const ORGANIZATIONS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.organizations',
      description: 'tutorials.manageOrganizationsTheirSub',
      showButtons: ['next', 'close'],
    },
  },
  {
    // Sits left of Add Organization in the header, so it is toured first.
    element: '#tour-organizations-import',
    popover: {
      title: 'tutorials.importOrganizations',
      description: 'tutorials.createManyOrganizationsOnce',
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.addOrganization',
      description: 'tutorials.createNewOrganization',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.organizationList',
      description: 'tutorials.browseManageOrganizationsSelect',
    },
  },
  {
    // The per-row cell, not the `<th>` header — and scoped under
    // `#tour-page-content` for the same reason as the Users page's Actions
    // step above (OrganizationPage.tsx's TableSkeleton reuses the same
    // `.actions-cell` class as the real table).
    element: '#tour-page-content .actions-cell',
    popover: {
      title: 'common.actions',
      description: 'tutorials.editDeleteOrganizationFrom',
    },
  },
  withCenteredHighlight({
    element: '#tour-organizations-pagination',
    popover: {
      title: 'tutorials.pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'tutorials.paginationOrganizations',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  }),
];

const SCENE_EDITOR_INTRO_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.sceneEditor',
      description: 'tutorials.configure3dClassroomScenes',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.promptConfig',
      description: 'tutorials.chooseWhichPromptConfiguration',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.sceneViewport',
      description: 'tutorials.preview3dClassroomScene',
    },
  },
];

interface SceneToolbarTab {
  id: 'animate' | 'camera' | 'assets' | 'layout' | 'triggers';
  /** Translation key. */
  label: string;
  /** Translation key. */
  description: string;
}

// The Animate/Camera/Assets/Layout tab bodies are all mounted at once inside
// `#tour-scene-content` (see SceneToolbar.tsx), toggled via `display` rather
// than conditional rendering — only one is ever visible at a time. A single
// step highlighting the shared container can't describe all four tools
// accurately (whichever tab happens to be open is the only one actually
// visible), so each tab gets its own click-gated step targeting its own
// content wrapper (`#tour-scene-tab-<id>`).
const SCENE_TOOLBAR_TABS: SceneToolbarTab[] = [
  { id: 'animate', label: 'tutorials.tabAnimate', description: 'tutorials.chooseCalmAggressiveAnimation' },
  { id: 'camera', label: 'tutorials.tabCamera', description: 'tutorials.adjustCameraPositionFraming' },
  { id: 'assets', label: 'tutorials.tabAssets', description: 'tutorials.manage3dAssetsUsed' },
  { id: 'layout', label: 'tutorials.tabLayout', description: 'tutorials.toggleEditModeThen' },
  // Only rendered while a prompt scene is selected, so buildSceneEditorSteps skips it when absent.
  { id: 'triggers', label: 'tutorials.tabTriggers', description: 'tutorials.bindTriggersAnimation' },
];

/**
 * The scene toolbar (`#tour-scene-toolbar`) always exists in the DOM, but
 * starts collapsed — its content panel only renders once a tab is clicked
 * (see SceneToolbar.tsx's `isOpen` state). This walks the tabs in order (the
 * four fixed ones, plus Triggers when a prompt scene is selected), gating each
 * on a click of that tab's button (mirroring the FTUE/Role Play
 * tours) before highlighting its now-visible content. Only the first tab in
 * the sequence can already be open when the tour starts (clicking a tab
 * always activates it — later tabs are guaranteed inactive once an earlier
 * one in this sequence has just been clicked), so only it needs an
 * already-active check to skip a redundant (and toggle-closing) click gate.
 */
function buildSceneEditorSteps(driverRef: React.RefObject<Driver | null>): DriveStep[] {
  const { gateOnClickThenAppear } = createGates(driverRef);
  const introSteps = filterExistingSteps(SCENE_EDITOR_INTRO_STEPS);

  const steps: DriveStep[] = [...introSteps];

  // The Triggers tab only exists while a prompt scene is selected, so its step is built from the DOM,
  // like the other conditional steps; the four fixed tabs are always walked. "Last" is the last tab
  // actually walked, so it carries the Finish button whether or not Triggers is there.
  const tabs = SCENE_TOOLBAR_TABS.filter(
    tab => tab.id !== 'triggers' || document.querySelector(`[data-testid="tab-${tab.id}"]`) !== null,
  );

  tabs.forEach((tab, i) => {
    const buttonSelector = `[data-testid="tab-${tab.id}"]`;
    const activeSelector = `${buttonSelector}.st__tab--active`;
    const alreadyActive = document.querySelector(activeSelector) !== null;

    if (!(i === 0 && alreadyActive)) {
      steps.push({
        element: buttonSelector,
        popover: {
          title: 'tutorials.sceneEditorTools',
          description: i18n.t('tutorials.clickTabToOpen', { label: i18n.t(tab.label) }),
          ...noNext,
        },
        ...gateOnClickThenAppear(i, buttonSelector, activeSelector),
      });
    }

    const isLast = i === tabs.length - 1;
    steps.push({
      element: `#tour-scene-tab-${tab.id}`,
      popover: {
        title: i18n.t('tutorials.tabTools', { value: i18n.t(tab.label) }),
        description: tab.description,
        // The toolbar panel (SceneToolbar.scss's `.st--open`) is right-docked
        // and spans the full height of `.scene-viewport`, but that viewport
        // itself starts below the page title/admin bar — real vertical room
        // exists there for driver.js's own placement logic to dock the
        // popover above the panel without overlapping it. An earlier fixed
        // top/left CSS override (forcing position regardless of this step's
        // actual content height) fought that and re-introduced the overlap
        // on the tallest of these four steps (the last one, with its extra
        // Finish-button footer) — prefer 'top' explicitly instead and let
        // driver.js measure real available space rather than guessing it.
        side: 'top',
        align: 'start',
        ...(isLast ? { showButtons: ['next', 'close'], doneBtnText: 'common.finish' } : {}),
      },
    });
  });

  return steps;
}

const APP_FEEDBACK_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.appFeedbackManagement',
      description: 'tutorials.seeFeedbackUsersHave',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.filters',
      description: 'tutorials.filterFeedbackSubmissions',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.feedbackList',
      description: 'tutorials.reviewSubmittedFeedback',
    },
  },
  {
    element: '#tour-feedback-actions-header',
    popover: {
      title: 'common.actions',
      description: 'tutorials.viewDetailsEditStatus',
    },
  },
  withCenteredHighlight({
    element: '#tour-feedback-pagination',
    popover: {
      title: 'tutorials.pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'tutorials.paginationFeedback',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  }),
];

const PROMPT_STUDIO_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.promptStudio',
      description: 'tutorials.authorEditAiPrompts',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.tabs',
      description: 'tutorials.switchBetweenPromptConfigs',
    },
  },
  {
    element: '#tour-prompt-create',
    popover: {
      title: 'tutorials.create',
      // Overwritten per-tab by buildPromptStudioSteps — see PROMPT_STUDIO_CREATE_COPY.
      description: 'tutorials.clickCreateNewPrompt',
    },
  },
  {
    // Triggers tab only (AnimationTriggersView): the prompt picker that scopes the second list. It lives in
    // the toolbar, which renders in every loading state, so it is always present on that tab.
    element: '#tour-trigger-prompt',
    popover: {
      title: 'tutorials.triggerPrompt',
      description: 'tutorials.chooseAPromptToManageTriggers',
    },
  },
  {
    // id passed to every Prompt Studio tab's <StatusFilter id="tour-prompt-filter" ...>.
    element: '#tour-prompt-filter',
    popover: {
      title: 'tutorials.filter',
      description: 'tutorials.filterListPublishedDraft',
    },
  },
  {
    // <HiddenFilter id="tour-prompt-hidden-filter"> — rendered for org admins only, on
    // the tabs whose platform items can be hidden; filterExistingSteps drops it elsewhere.
    element: '#tour-prompt-hidden-filter',
    popover: {
      title: 'Hidden Items',
      description:
        'Show all items, only what your organization sees, or only the platform items you have '
        + 'hidden from it. Pick Hidden to find one and unhide it.',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.editor',
      description: 'tutorials.buildConfigureItems',
    },
  },
  {
    // Triggers tab only: the Organization defaults / This prompt lists. Absent while the tab is loading,
    // failed or empty, so filterExistingSteps drops it then.
    element: '#tour-trigger-groups',
    popover: {
      title: 'tutorials.triggerGroups',
      description: 'tutorials.triggerGroupsDescription',
    },
  },
  {
    // Root class of EntityCard.tsx's actions row, present on every card in the
    // list — driver.js highlights the first match as a representative example.
    // Not present on the Evaluations tab (its cards use a different markup),
    // so filterExistingSteps drops this step there — see buildPromptStudioSteps.
    element: '.pc-entity-card__actions-row',
    popover: {
      title: 'tutorials.itemActions',
      // Role- and item-dependent, so this is only the fallback: buildPromptStudioSteps
      // narrows it to the buttons actually rendered. Anything said here has to hold for
      // every viewer, because it is what shows when the DOM cannot be read.
      description: 'tutorials.iconButtonsManagingEach',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
  {
    // Triggers tab only: first row's edit/archive buttons. Triggers are not EntityCards, so the step above
    // is dropped there and this one takes its place.
    element: '.trg-row__actions',
    popover: {
      title: 'tutorials.itemActions',
      description: 'tutorials.triggerRowActions',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
];

// Each Prompt Studio tab renders a different embedded view with its own
// create action (see PromptConfigsView/BuildingBlocksView/SchemaManagerView/
// EvalSuitesView), but they all share the same `#tour-prompt-create` button —
// so its popover copy must match whichever tab is actually active rather than
// listing all five generically (which also previously omitted Evaluations
// entirely, since it wasn't part of the original static description).
const PROMPT_STUDIO_CREATE_COPY: Record<string, string> = {
  prompts: 'tutorials.clickCreateNewPrompt',
  'content-blocks': 'tutorials.clickCreateContentBlock',
  templates: 'tutorials.clickCreateTemplate',
  evaluations: 'tutorials.clickCreateEvaluationSuite',
  triggers: 'tutorials.clickCreateTrigger',
};

function buildPromptStudioSteps(): DriveStep[] {
  const activeTab = new URLSearchParams(window.location.search).get('tab') ?? 'prompts';
  const createDescription = PROMPT_STUDIO_CREATE_COPY[activeTab] ?? PROMPT_STUDIO_CREATE_COPY.prompts;

  const steps = filterExistingSteps(
    PROMPT_STUDIO_STEPS.map((step) =>
      step.element === '#tour-prompt-create'
        ? { ...step, popover: { ...step.popover, description: createDescription } }
        : step
    )
  );

  // The Evaluations tab lacks the filter and edit/delete steps' anchors, so
  // they're filtered out above — make sure whichever step actually ends up
  // last still closes the tour properly.
  const last = steps[steps.length - 1];
  if (last?.popover) {
    last.popover = { ...last.popover, showButtons: ['next', 'close'], doneBtnText: 'common.finish' };
  }

  return describeCardActions(steps);
}

/**
 * Narrow the card-actions step to the buttons this viewer can actually see.
 *
 * EntityCard renders its actions per row, from the viewer's role and the item's
 * ownership: an org admin gets Duplicate plus Hide/Unhide on a platform item and
 * Edit/Delete on their own, while a super admin gets Duplicate/Edit/Delete and never
 * Hide (`canHidePlatform` is `userRole === 'admin'`). filterExistingSteps only checks
 * that the row itself exists, so fixed copy would name controls the viewer does not
 * have and deny ones they do. Same approach as buildReviewerDashboardSteps.
 */
function describeCardActions(steps: DriveStep[]): DriveStep[] {
  // Matched on data-action, not the rendered title, so it holds in every language.
  const actions = new Set(
    Array.from(document.querySelectorAll<HTMLElement>('.pc-entity-card__actions-row button')).map(
      (button) => button.dataset.action,
    ),
  );

  const clauses: string[] = [];
  if (actions.has('edit')) clauses.push(i18n.t('tutorials.clauseEditItem'));
  if (actions.has('duplicate')) clauses.push(i18n.t('tutorials.clauseDuplicateItem'));
  if (actions.has('hide')) clauses.push(i18n.t('tutorials.clauseHideItem'));
  if (actions.has('unhide')) clauses.push(i18n.t('tutorials.clauseUnhideItem'));
  if (actions.has('delete')) clauses.push(i18n.t('tutorials.clauseDeleteItem'));

  // No cards, or no recognised buttons: keep the general copy rather than narrowing it
  // to something possibly wrong.
  if (clauses.length === 0) return steps;

  return reviseStep(steps, '.pc-entity-card__actions-row', {
    description: `${sentence(joinClauses(clauses))}.`,
  });
}

const REVIEW_MANAGEMENT_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.reviews',
      description: 'tutorials.manageHumanReviewRequests',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.search',
      description: 'tutorials.searchReviewRequestsModule',
    },
  },
  {
    element: '#tour-review-tabs',
    popover: {
      title: 'tutorials.filterStatus',
      description: 'tutorials.narrowListDownAll',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.reviewList',
      description: 'tutorials.browseReviewRequests',
    },
  },
  {
    element: '.review-mgmt__kebab',
    popover: {
      title: 'common.actions',
      description: 'tutorials.viewDetailsAssignAdd',
    },
  },
  withCenteredHighlight({
    element: '#tour-reviews-pagination',
    popover: {
      title: 'tutorials.pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'tutorials.paginationReviews',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  }),
];

const REVIEWER_DASHBOARD_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.myReviews',
      description: 'tutorials.reviewRoleplaySessionsAssigned',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.filterStatus',
      description: 'tutorials.clickStatCardFilter',
    },
  },
  {
    element: '#tour-reviewer-toolbar',
    popover: {
      title: 'tutorials.searchFilters',
      description: 'tutorials.searchSessionFilterPriority',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.assignedSessions',
      // Rewritten by buildReviewerDashboardSteps from the buttons actually on
      // screen; this copy is the all-statuses case.
      description: 'tutorials.claimPendingSessionWork',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
  withCenteredHighlight({
    element: '#tour-reviewer-dashboard-pagination',
    popover: {
      title: 'tutorials.pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'tutorials.paginationSessions',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  }),
];

/**
 * One button per row, and which one depends on that row's status (see
 * ReviewerDashboardPage's actions cell): "Assign to me" for pending, "Review"
 * for anything in progress, "View" for completed. The status filter therefore
 * decides what exists — on the Completed card only "View" is ever rendered, so
 * fixed copy naming "Assign to me" and "Review" described two buttons that
 * weren't there and omitted the only one that was.
 */
function buildReviewerDashboardSteps(): DriveStep[] {
  const steps = filterExistingSteps(REVIEWER_DASHBOARD_STEPS);

  const actions = Array.from(document.querySelectorAll<HTMLElement>('.reviewer-dash__actions button')).map(
    (button) => button.dataset.action,
  );
  const clauses: string[] = [];
  // Matched on data-action (ReviewerDashboardPage), not the rendered label, so this
  // works in every language.
  if (actions.includes('assign')) clauses.push(i18n.t('tutorials.clauseAssignToMe'));
  if (actions.includes('review')) clauses.push(i18n.t('tutorials.clauseOpenAssigned'));
  if (actions.includes('view')) clauses.push(i18n.t('tutorials.clauseReadReview'));

  // No rows, or an unrecognised set of labels: leave the general copy alone
  // rather than narrowing it to something possibly wrong.
  if (clauses.length === 0) return steps;

  return reviseStep(steps, '#tour-page-content', {
    description: `${sentence(joinClauses(clauses))}.`,
  });
}

interface GenericPageCopy {
  title: { title: string; description: string };
  primaryAction: { title: string; description: string };
  content: { title: string; description: string };
}

// Copy for the lightweight, consistent 3-step tour shared by every other
// nav-linked page: title -> primary action -> main content.
const GENERIC_PAGE_COPY: Record<string, GenericPageCopy> = {
  '/team-progress': {
    title: { title: 'tutorials.teamProgress', description: 'tutorials.trackXpStreaksProgress' },
    primaryAction: { title: 'tutorials.periodExport', description: 'tutorials.changeTimePeriodExport' },
    content: { title: 'tutorials.traineeRoster', description: 'tutorials.seeEachTraineeProgress' },
  },
  '/classes': {
    title: { title: 'tutorials.classes', description: 'tutorials.classesPage' },
    primaryAction: { title: 'tutorials.studentAdminTabs', description: 'tutorials.studentAdminTabsDesc' },
    content: { title: 'tutorials.studentAdminContent', description: 'tutorials.studentAdminContentDesc' },
  },
  '/my-grades': {
    title: { title: 'tutorials.myGrades', description: 'tutorials.myGradesPage' },
    primaryAction: { title: 'tutorials.releasedGrades', description: 'tutorials.releasedGradesDesc' },
    content: { title: 'tutorials.gradeDetails', description: 'tutorials.gradeDetailsDesc' },
  },
  '/my-practice': {
    title: { title: 'tutorials.myPractice', description: 'tutorials.myPracticePage' },
    primaryAction: { title: 'tutorials.practiceToDo', description: 'tutorials.practiceToDoDesc' },
    content: { title: 'tutorials.howPracticeWorks', description: 'tutorials.howPracticeWorksDesc' },
  },
};

const MY_ASSIGNMENTS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.myAssignments',
      description: 'tutorials.trackAssignedTrainingBundles',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.search',
      description: 'tutorials.searchAssignments',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.assignmentList',
      description: 'tutorials.viewAssignedTraining',
    },
  },
  {
    // Not the '#tour-my-assignments-actions-header' <th> — that's the table's
    // header row, which _assignment-shared.scss visually-hides (not
    // display:none) below 720px, so it's still DOM-present but collapses to
    // a near-zero-size box in the mobile card layout, breaking the highlight.
    // The row-level `.assign-actions-cell` <td> is visible in both layouts.
    element: '.assign-actions-cell',
    popover: {
      title: 'common.actions',
      description: 'tutorials.clickStartReviewBegin',
    },
  },
  withCenteredHighlight({
    element: '#tour-my-assignments-pagination',
    popover: {
      title: 'tutorials.pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'tutorials.paginationAssignments',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  }),
];

const ASSIGNMENTS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.assignments',
      description: 'tutorials.createManageTrainingAssignments',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.newAssignment',
      description: 'tutorials.createNewAssignment',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.assignmentList',
      description: 'tutorials.browseManageAssignments',
    },
  },
  {
    // See the comment on the equivalent My Assignments step above —
    // '.assign-actions-cell' (not the visually-hidden <th> below 720px).
    element: '.assign-actions-cell',
    popover: {
      title: 'common.actions',
      description: 'tutorials.viewEditPublishArchive',
    },
  },
  withCenteredHighlight({
    element: '#tour-assignments-pagination',
    popover: {
      title: 'tutorials.pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'tutorials.paginationAssignments',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  }),
];

const BUNDLES_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.bundles',
      description: 'tutorials.createManageContentBundles',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.newBundle',
      description: 'tutorials.createNewBundle',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.bundleList',
      description: 'tutorials.browseManageBundles',
    },
  },
  {
    // See the comment on the equivalent My Assignments step above —
    // '.assign-actions-cell' (not the visually-hidden <th> below 720px).
    element: '.assign-actions-cell',
    popover: {
      title: 'common.actions',
      description: 'tutorials.editPublishArchiveBundle',
    },
  },
  withCenteredHighlight({
    element: '#tour-bundles-pagination',
    popover: {
      title: 'tutorials.pagination',
      // Pagination bars sit at the bottom of these tables with little to no
      // room below for driver.js's default placement, which otherwise
      // overlaps the highlighted control instead of flipping above it.
      side: 'top',
      description: 'tutorials.paginationBundles',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  }),
];

const COMPETITIONS_INTRO_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.competitions',
      description: 'tutorials.joinManageCompetitionsTrack',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.scheduleCompetition',
      description: 'tutorials.createNewCompetitionTime',
    },
  },
  {
    element: '#tour-competitions-tabs',
    popover: {
      title: 'tutorials.tabs',
      description: 'tutorials.switchBetweenLiveCompetitions',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.competitionList',
      description: 'tutorials.browseCompetitionsSeeTheir',
    },
  },
];

// The Actions column header always renders once the table does, but which
// row-level buttons appear under it depends on both the active tab and the
// viewer's relationship to each competition (see CompetitionsPage.tsx's
// actions-cell): Start/Continue is hidden for competitions the current user
// created, Edit and Cancel/End-Now only show for a manager on a scheduled/
// active, non-read-only competition — so on the Ended Competitions tab none
// of them ever appear, leaving only the Leaderboard button.
// filterExistingSteps' DOM-presence check on the header alone can't
// see any of this, so the copy is built from which action buttons are
// actually present, not just whether the header exists.
function buildCompetitionsSteps(): DriveStep[] {
  const steps = filterExistingSteps(COMPETITIONS_INTRO_STEPS);

  if (document.querySelector('#tour-competitions-actions-header') === null) {
    return steps;
  }

  // The buttons' titles are translated (CompetitionsPage), so look them up by the same keys.
  const hasButton = (className: string, ...titleKeys: string[]) =>
    titleKeys.some((key) => document.querySelector(`.${className}[title="${i18n.t(key).replace(/["\\]/g, '\\$&')}"]`) !== null);
  const hasStartOrContinue = hasButton('edit-button', 'competitions.start', 'common.continue');
  const hasEdit = hasButton('edit-button', 'competitions.editCompetitionTitle');
  const hasCancelOrEnd = hasButton('delete-button', 'competitions.cancelCompetitionTitle', 'competitions.endCompetitionNowTitle');

  const clauses = [i18n.t('tutorials.clauseViewLeaderboard')];
  if (hasStartOrContinue) clauses.unshift(i18n.t('tutorials.clauseStartCompetition'));
  if (hasEdit) clauses.push(i18n.t('tutorials.clauseEditCompetition'));
  if (hasCancelOrEnd) clauses.push(i18n.t('tutorials.clauseCancelCompetition'));

  const description = sentence(i18n.t('tutorials.fromActionsColumn', { actions: joinClauses(clauses) }));

  return [
    ...steps,
    {
      element: '#tour-competitions-actions-header',
      popover: {
        title: 'common.actions',
        description,
        showButtons: ['next', 'close'],
        doneBtnText: 'common.finish',
      },
    },
  ];
}

const CHARACTERS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.characters',
      description: 'tutorials.aiStudentsUsedRoleplay',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.addCharacter',
      description: 'tutorials.createNewCharacterName',
    },
  },
  {
    element: '#tour-manage-social-skills',
    popover: {
      title: 'tutorials.socialSkills',
      description: 'tutorials.createSocialSkillsCharacter',
    },
  },
  {
    element: '#tour-characters-rail',
    popover: {
      title: 'tutorials.findCharacter',
      description: 'tutorials.searchNameFilterCharacters',
    },
  },
  {
    // The detail pane, not the tab strip inside it: the tabs only render once a
    // character is selected, and an empty roster would strip the step entirely.
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.characterSettings',
      description: 'tutorials.tuneVoicePersonalityBig',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
];

const PLATFORM_DASHBOARD_STEPS: DriveStep[] = [
  {
    element: '.platform-dash-stat-grid',
    popover: {
      title: 'tutorials.platformOverview',
      description: 'tutorials.highLevelStatsAcross',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.tabs',
      description: 'tutorials.switchBetweenOrganizationsUsers',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.details',
      description: 'tutorials.exploreOrganizationUserDetail',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
];

/**
 * Home dashboard catalog. Home renders a different composition per role
 * (trainee / trainer / admin / super_admin), so there is no single fixed step
 * list that fits everyone. Rather than duplicating the role switch from
 * HomePage.tsx here (a second source of truth that would drift), this is a
 * superset catalog: every block any role can see gets an entry, and
 * buildHomeSteps drops the ones absent from the DOM.
 *
 * Safe to filter on DOM presence because Home renders its whole composition
 * at mount. Nothing here is progressive or behind a click, unlike the Role
 * Play wizard tour, so there is no need for gates.
 */
const HOME_STEPS: DriveStep[] = [
  {
    element: '#tour-home-greeting',
    popover: {
      title: 'tutorials.yourHome',
      description: 'tutorials.homeShowsCurrentState',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-home-hero',
    popover: {
      title: 'tutorials.startPractising',
      description: 'tutorials.jumpStraightIntoRole',
    },
  },
  {
    element: '#tour-home-practice-strip',
    popover: {
      title: 'tutorials.yourPracticeTitle',
      description: 'tutorials.ownWeeklyPracticeProgress',
    },
  },
  {
    element: '#tour-home-attention',
    popover: {
      title: 'common.needsYourAttention',
      description: 'tutorials.anythingWaitingPersonSessions',
    },
  },
  {
    element: '#tour-home-all-clear',
    popover: {
      title: 'tutorials.allClear',
      description: 'tutorials.nothingWaitingRightNow',
    },
  },
  {
    element: '#tour-home-momentum',
    popover: {
      title: 'common.yourMomentum',
      description: 'tutorials.streakBadgesSessionsXp',
    },
  },
  {
    element: '#tour-home-team',
    popover: {
      title: 'common.teamSnapshot',
      description: 'tutorials.howTraineesDoingWeek',
    },
  },
  {
    element: '#tour-home-org-health',
    popover: {
      title: 'common.organizationHealth',
      description: 'tutorials.seatUsageUserCount',
    },
  },
  {
    element: '#tour-home-platform',
    popover: {
      title: 'common.platformSnapshot',
      description: 'tutorials.totalsAcrossEveryOrganization',
    },
  },
  {
    element: '#tour-home-top-orgs',
    popover: {
      title: 'common.mostActiveOrganizations',
      description: 'tutorials.organizationsRunningMostSessions',
    },
  },
  {
    element: '#tour-home-overdue',
    popover: {
      title: 'common.overdue',
      description: 'tutorials.assignmentsWhoseDueDate',
    },
  },
  {
    element: '#tour-home-assignments',
    popover: {
      title: 'common.ongoingAssignments',
      description: 'tutorials.assignmentsHaveStartedNot',
    },
  },
  {
    element: '#tour-home-deadlines',
    popover: {
      title: 'common.upcomingDeadlines',
      description: 'tutorials.assignmentsDueDateStill',
    },
  },
  {
    element: '#tour-home-actions',
    popover: {
      title: 'common.quickActions',
      description: 'tutorials.shortcutsTasksRolePerforms',
    },
  },
];

/**
 * Orders the surviving Home steps by their real position in the document.
 *
 * The catalog above cannot be written in one "correct" order, because each
 * role stacks these blocks differently (a trainee leads with the practice
 * hero, an admin leads with org health). Sorting by document position means
 * the tour always walks down the page in the order the user actually sees,
 * for every role, without this file knowing anything about roles.
 */
function buildHomeSteps(): DriveStep[] {
  const present = filterExistingSteps(HOME_STEPS);

  const ordered = [...present].sort((a, b) => {
    const elA = document.querySelector(a.element as string);
    const elB = document.querySelector(b.element as string);
    if (!elA || !elB) return 0;
    // DOCUMENT_POSITION_FOLLOWING (4) means elB comes after elA.
    return elA.compareDocumentPosition(elB) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });

  // Whichever block ends up last for this role is the one that closes the
  // tour, so the Finish button has to be attached after sorting.
  const last = ordered[ordered.length - 1];
  if (last) {
    ordered[ordered.length - 1] = {
      ...last,
      popover: { ...last.popover, showButtons: ['next', 'close'], doneBtnText: 'common.finish' },
    };
  }

  return ordered;
}

function buildGenericSteps(copy: GenericPageCopy): DriveStep[] {
  return [
    {
      element: '#tour-page-title',
      popover: { title: copy.title.title, description: copy.title.description, showButtons: ['next', 'close'] },
    },
    {
      element: '#tour-page-primary-action',
      popover: { title: copy.primaryAction.title, description: copy.primaryAction.description },
    },
    {
      element: '#tour-page-content',
      popover: { title: copy.content.title, description: copy.content.description, showButtons: ['next', 'close'], doneBtnText: 'common.finish' },
    },
  ];
}

const REPORTS_STEPS: DriveStep[] = [
  {
    element: '.user-report-stat-grid',
    popover: {
      title: 'tutorials.userReport',
      description: 'tutorials.trackTrialSubscribedRenewal',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.tabs',
      description: 'tutorials.switchBetweenTrialSubscribed',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.userList',
      description: 'tutorials.filterOrganizationPlanJoined',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
];

const JOIN_REQUESTS_STEPS: DriveStep[] = [
  {
    element: '#tour-page-title',
    popover: {
      title: 'tutorials.joinRequests',
      description: 'tutorials.peopleAskingJoinOrganization',
      showButtons: ['next', 'close'],
    },
  },
  {
    element: '#tour-join-settings-toggle',
    popover: {
      title: 'tutorials.settings',
      description: 'tutorials.claimOrganizationEmailDomain',
    },
  },
  {
    element: '#tour-page-primary-action',
    popover: {
      title: 'tutorials.seats',
      description: 'tutorials.howManySeatsFree',
    },
  },
  {
    element: '#tour-page-content',
    popover: {
      title: 'tutorials.requests',
      description: 'tutorials.decidePendingRequestsMove',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
];

/**
 * The Settings panel (JoinSettingsCard) toggles via the button above rather
 * than unmounting the queue below it — the seat badge and request tabs stay
 * in the DOM with `display: none` while it's open (JoinRequestsPage.tsx), so
 * filterExistingSteps' presence-only check can't tell they're actually
 * invisible. Highlighting them in that state would target elements driver.js
 * can't measure. So when the toggle is already expanded at tour-open time,
 * those two steps are dropped and the Settings step becomes the closing one
 * instead.
 */
function buildJoinRequestsSteps(): DriveStep[] {
  const steps = filterExistingSteps(JOIN_REQUESTS_STEPS).map((step) => ({ ...step }));
  const settingsOpen =
    document.querySelector('#tour-join-settings-toggle')?.getAttribute('aria-expanded') === 'true';
  if (!settingsOpen) return steps;

  const trimmed = steps.filter(
    (step) => step.element !== '#tour-page-primary-action' && step.element !== '#tour-page-content'
  );
  const last = trimmed[trimmed.length - 1];
  if (last?.popover) {
    last.popover = { ...last.popover, showButtons: ['next', 'close'], doneBtnText: 'common.finish' };
  }
  return trimmed;
}

const BESPOKE_STEPS: Record<string, DriveStep[]> = {
  '/dashboard': ANALYTICS_DASHBOARD_STEPS,
  '/users': USERS_STEPS,
  '/organizations': ORGANIZATIONS_STEPS,
  '/reports': REPORTS_STEPS,
  '/feedback': APP_FEEDBACK_STEPS,
  '/reviews': REVIEW_MANAGEMENT_STEPS,
  '/my-assignments': MY_ASSIGNMENTS_STEPS,
  '/assignments': ASSIGNMENTS_STEPS,
  '/bundles': BUNDLES_STEPS,
  '/platform-dashboard': PLATFORM_DASHBOARD_STEPS,
  '/characters': CHARACTERS_STEPS,
};

/**
 * Any step that can legitimately *end* a tour carries the closing treatment
 * statically — `showButtons: ['next', 'close']` plus a Finish label — so the
 * tour still closes properly when the steps after it were filtered out for
 * not being in the DOM (e.g. the reviewer dashboard's session-list step,
 * which is last only when the pagination bar isn't rendered). When those
 * later steps *are* present, that same treatment also hides the step's Back
 * button, stranding the user mid-tour with Next as the only way to move.
 *
 * Put Back on every step except the first (driver.js disables it there
 * anyway, and these tours hide it outright) and the last (the closing step,
 * deliberately Finish/close only). Click-gated tours are left alone: their
 * steps hide Next because the user has to perform the action to advance, and
 * a gate that has already fired can't be re-armed by stepping back into it.
 */
function withBackButtons(steps: DriveStep[]): DriveStep[] {
  const isGated = steps.some((step) => step.popover?.showButtons && !step.popover.showButtons.includes('next'));
  if (isGated) return steps;

  return steps.map((step, index) => {
    const showButtons = step.popover?.showButtons;
    if (index === 0 || index === steps.length - 1) return step;
    if (!showButtons || showButtons.includes('previous')) return step;
    return { ...step, popover: { ...step.popover, showButtons: ['previous', ...showButtons] } };
  });
}

const SESSION_DETAIL_PATTERN = /^\/sessions\/[^/]+$/;
// Student system: reviewing and grading one attempt (AttemptReviewPage).
const ATTEMPT_REVIEW_PATTERN = /^\/classes\/[^/]+\/attempts\/[^/]+$/;
const ATTEMPT_REVIEW_COPY: GenericPageCopy = {
  title: { title: 'tutorials.attemptReview', description: 'tutorials.attemptReviewPage' },
  primaryAction: { title: 'tutorials.gradeAndRelease', description: 'tutorials.gradeAndReleaseDesc' },
  content: { title: 'tutorials.attemptEvidence', description: 'tutorials.attemptEvidenceDesc' },
};

export function getPageSteps(pathname: string, driverRef: React.RefObject<Driver | null>): DriveStep[] {
  return localizeSteps(withBackButtons(resolvePageSteps(pathname, driverRef)));
}

function resolvePageSteps(pathname: string, driverRef: React.RefObject<Driver | null>): DriveStep[] {
  if (pathname === '/') {
    // '/' is the Home dashboard. Role Play moved to '/roleplay' when Home
    // took over the landing route, so the Role Play tour moved with it.
    return buildHomeSteps();
  }
  if (pathname === '/roleplay') {
    // Deliberately NOT run through filterExistingSteps: this is a
    // progressive, gated wizard-style tour (mirrors the FTUE interactive
    // tour) whose step targets intentionally don't all exist at once. Each
    // one only appears once the step(s) before it are completed (e.g. the
    // mic button doesn't exist until the classroom step's avatar click
    // connects a session). A static, build-time DOM-presence filter would
    // incorrectly strip out every step beyond whatever's visible right now.
    return buildRolePlayPageSteps(driverRef);
  }
  if (pathname === '/sessions') {
    // Not a plain filterExistingSteps — see buildSessionsListSteps' own comment
    // for why the Actions step's copy depends on more than the header existing.
    return buildSessionsListSteps();
  }
  if (ATTEMPT_REVIEW_PATTERN.test(pathname)) {
    return filterExistingSteps(buildGenericSteps(ATTEMPT_REVIEW_COPY));
  }
  if (SESSION_DETAIL_PATTERN.test(pathname)) {
    // Not run through filterExistingSteps as a whole — see
    // buildSessionDetailSteps's own comment for why the transcript block
    // needs special handling.
    return buildSessionDetailSteps(driverRef);
  }
  if (pathname === '/scene-editor') {
    // Not run through filterExistingSteps for its tail end — see
    // buildSceneEditorSteps's own comment for why.
    return buildSceneEditorSteps(driverRef);
  }
  if (pathname === '/prompt-studio') {
    // Not a plain BESPOKE_STEPS entry — the Create step's copy depends on
    // which tab is active. See buildPromptStudioSteps's own comment.
    return buildPromptStudioSteps();
  }
  if (pathname === '/competitions') {
    // Not run through filterExistingSteps as a whole — see
    // buildCompetitionsSteps's own comment for why the Actions step needs
    // its own DOM check beyond the header's presence.
    return buildCompetitionsSteps();
  }
  if (pathname === '/reviewer-dashboard') {
    // Not run through filterExistingSteps as a whole — see
    // buildReviewerDashboardSteps' own comment for why the row-action copy
    // depends on which statuses are currently listed.
    return buildReviewerDashboardSteps();
  }
  if (pathname === '/join-requests') {
    // Not a plain BESPOKE_STEPS entry — see buildJoinRequestsSteps' own
    // comment for why the Seats/Requests steps depend on whether the
    // Settings panel is currently open.
    return buildJoinRequestsSteps();
  }
  if (pathname === '/my-progress') {
    // Not run through filterExistingSteps as a whole — see
    // buildMyProgressSteps's own comment for why the Overview/Leaderboard/
    // Badges tabs each need their own step set.
    return buildMyProgressSteps();
  }

  const bespokeSteps = BESPOKE_STEPS[pathname];
  if (bespokeSteps) {
    return filterExistingSteps(bespokeSteps);
  }

  const genericCopy = GENERIC_PAGE_COPY[pathname];
  if (genericCopy) {
    return filterExistingSteps(buildGenericSteps(genericCopy));
  }

  return [];
}
